import { and, eq } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { auditLog, knowledgeEntries } from '@/db/schema'
import { buildKnowledgeEmbedding } from '@/lib/knowledge'
import {
  KNOWLEDGE_EMBEDDING_MODEL,
} from '@/db/schema'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type IndexKnowledgeEntryInput = {
  entryId: string
  tenantId: string
  action: 'created' | 'updated'
}

export async function runKnowledgeIndexer(
  input: IndexKnowledgeEntryInput,
  options?: {
    db?: DB
    embed?: (text: string) => number[]
  },
): Promise<{ outcome: 'indexed' | 'missing' }> {
  const db = (options?.db ?? defaultDb) as DB
  const embed = options?.embed ?? buildKnowledgeEmbedding

  const [entry] = await db
    .select()
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.id, input.entryId), eq(knowledgeEntries.tenantId, input.tenantId)))
    .limit(1)

  if (!entry) return { outcome: 'missing' }

  const embedding = embed(`${entry.title}\n${entry.flatMarkdown}`)
  await db
    .update(knowledgeEntries)
    .set({
      embedding,
      embeddingModel: KNOWLEDGE_EMBEDDING_MODEL,
      embeddingUpdatedAt: new Date(),
      updatedAt: new Date(),
    })
    .where(and(eq(knowledgeEntries.id, input.entryId), eq(knowledgeEntries.tenantId, input.tenantId)))

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: null,
    actorType: 'ai_agent',
    action: 'knowledge.embedding_indexed',
    entityType: 'knowledge_entry',
    entityId: input.entryId,
    payload: {
      source_event: input.action,
      embedding_model: KNOWLEDGE_EMBEDDING_MODEL,
      reasoning_trace: 'Deterministic local text embedding generated from the entry title and flat markdown body.',
    },
  })

  return { outcome: 'indexed' }
}

export const knowledgeIndexerFn = inngest.createFunction(
  { id: 'knowledge-indexer', retries: 3, triggers: [{ event: 'knowledge/entry.changed' }] },
  async ({ event, step }) => {
    return await step.run('index-knowledge-entry', () =>
      runKnowledgeIndexer(event.data as IndexKnowledgeEntryInput),
    )
  },
)
